import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Heart, Calendar, Users, DollarSign, Trophy, MapPin, Clock, Sparkles, UserPlus, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';

const SinglesNight = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentWeek, setCurrentWeek] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [rsvpCount, setRsvpCount] = useState(0);
  const [hasRsvp, setHasRsvp] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  
  // Bid form state
  const [bidForm, setBidForm] = useState({
    bid_amount: 100,
    max_capacity: 100,
    drink_specials: '',
    description: ''
  });

  useEffect(() => {
    fetchSinglesNightData();
  }, []);

  const fetchSinglesNightData = async () => {
    try {
      const [currentRes, upcomingRes] = await Promise.all([
        axios.get(`${API}/singles-night/current-week`),
        axios.get(`${API}/singles-night/upcoming`)
      ]);
      
      setCurrentWeek(currentRes.data);
      setUpcoming(upcomingRes.data);
      
      // Fetch RSVP count
      try {
        const rsvpRes = await axios.get(`${API}/singles-night/rsvp/${currentRes.data.week_date}`);
        setRsvpCount(rsvpRes.data.rsvp_count || 0);
      } catch (e) { /* ignore */ }
      
      // Check user RSVP
      if (user) {
        try {
          const token = localStorage.getItem('token');
          const myRsvp = await axios.get(`${API}/singles-night/my-rsvp/${currentRes.data.week_date}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setHasRsvp(myRsvp.data.has_rsvp);
        } catch (e) { /* ignore */ }
      }
      
      // Fetch leaderboard if bidding is open
      if (!currentRes.data.has_winner) {
        const leaderboardRes = await axios.get(
          `${API}/singles-night/leaderboard/${currentRes.data.week_date}`
        );
        setLeaderboard(leaderboardRes.data.leaderboard || []);
      }
    } catch (error) {
      console.log('Singles Night data not available');
      // Demo data
      const nextSaturday = getNextSaturday();
      setCurrentWeek({
        week_date: nextSaturday,
        has_winner: false,
        bidding_ends: `${nextSaturday}T12:00:00Z`,
        current_bids: 5,
        highest_bid: 250
      });
      setLeaderboard([
        { rank: 1, bar_name: 'The Blue Lounge', bid_amount: 250, max_capacity: 150 },
        { rank: 2, bar_name: 'Neon Nights', bid_amount: 200, max_capacity: 120 },
        { rank: 3, bar_name: 'Rooftop Bar', bid_amount: 175, max_capacity: 100 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getNextSaturday = () => {
    const today = new Date();
    const daysUntilSaturday = (6 - today.getDay()) % 7 || 7;
    const nextSat = addDays(today, daysUntilSaturday);
    return format(nextSat, 'yyyy-MM-dd');
  };

  const handlePlaceBid = async () => {
    if (!selectedWeek) return;
    
    try {
      await axios.post(`${API}/singles-night/bid`, {
        week_date: selectedWeek,
        ...bidForm
      });
      toast.success('Bid placed successfully!');
      setBidDialogOpen(false);
      fetchSinglesNightData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place bid');
    }
  };

  const openBidDialog = (weekDate) => {
    setSelectedWeek(weekDate);
    setBidDialogOpen(true);
  };

  const handleRsvp = async () => {
    if (!user) { toast.error('Log in to RSVP'); return; }
    setRsvpLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/singles-night/rsvp`,
        { week_date: currentWeek.week_date },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status === 'confirmed') {
        setHasRsvp(true);
        setRsvpCount(c => c + 1);
        toast.success("You're in! See you Saturday.");
      } else {
        setHasRsvp(false);
        setRsvpCount(c => Math.max(0, c - 1));
        toast.success('RSVP cancelled');
      }
    } catch (e) {
      toast.error('Failed to RSVP');
    } finally {
      setRsvpLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass p-6 border border-pink-500/30">
        <div className="flex items-center gap-2 text-pink-400">
          <Heart className="w-5 h-5 animate-pulse" />
          <span>Loading Singles Night...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="glass p-6 border border-pink-500/30">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center">
            <Heart className="w-6 h-6 text-white" fill="white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Singles Night</h2>
            <p className="text-sm text-slate-400">Weekly event at the winning bar</p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1 bg-pink-500/20 rounded-full">
          <Sparkles className="w-4 h-4 text-pink-400" />
          <span className="text-sm text-pink-400">Every Saturday</span>
        </div>
      </div>

      {/* Current Week Status */}
      {currentWeek && (
        <div className="mb-6">
          {currentWeek.has_winner ? (
            /* Winner Announced */
            <div className="p-4 bg-gradient-to-r from-pink-500/20 to-red-500/20 rounded-lg border border-pink-500/30">
              <div className="mb-3 -mx-4 -mt-4 px-4 py-2 bg-gradient-to-r from-yellow-500 to-pink-500 text-black text-xs font-black tracking-widest text-center rounded-t-lg">
                ★ OFFICIAL SINGLES NIGHT VENUE ★
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-5 h-5 text-yellow-400" />
                <span className="font-semibold text-white">This Week&apos;s Host</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg bg-slate-800 flex items-center justify-center">
                  <MapPin className="w-8 h-8 text-pink-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">
                    {currentWeek.winning_bar?.name || 'TBA'}
                  </h3>
                  <p className="text-sm text-slate-400">
                    {currentWeek.winning_bar?.address || 'Address TBA'}
                  </p>
                  <p className="text-sm text-pink-400 mt-1">
                    {format(new Date(currentWeek.week_date), 'EEEE, MMMM d')} • 8PM - 2AM
                  </p>
                </div>
              </div>
              {currentWeek.event_details && (
                <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-sm text-slate-300">
                    🍸 {currentWeek.event_details.drink_specials || 'Drink specials all night!'}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    👥 Capacity: {currentWeek.event_details.max_capacity} people
                  </p>
                </div>
              )}
              <Button 
                className="mt-4 w-full bg-gradient-to-r from-pink-500 to-red-500"
                onClick={() => navigate(`/bar/${currentWeek.winning_bar?.id}`)}
              >
                View Bar Details
              </Button>
              <div className="flex gap-2 mt-2">
                <Button
                  data-testid="rsvp-btn"
                  className={`flex-1 ${hasRsvp ? 'bg-green-600 hover:bg-green-700' : 'bg-pink-600 hover:bg-pink-700'}`}
                  onClick={handleRsvp}
                  disabled={rsvpLoading}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  {hasRsvp ? `RSVP'd (${rsvpCount})` : `RSVP (${rsvpCount} going)`}
                </Button>
                <Button
                  variant="outline"
                  className="border-pink-500/50 text-pink-400"
                  onClick={() => {
                    const text = `I'm going to Singles Night at ${currentWeek.winning_bar?.name || 'NightBuZz'}! Join me! #NightBuZz #SinglesNight`;
                    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
                  }}
                >
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            /* Bidding Open */
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-400" />
                  <span className="font-semibold text-white">Bidding Open</span>
                </div>
                <span className="text-sm text-amber-400">
                  {format(new Date(currentWeek.week_date), 'MMM d')}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-400">${currentWeek.highest_bid}</p>
                  <p className="text-xs text-slate-400">Highest Bid</p>
                </div>
                <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-400">{currentWeek.current_bids}</p>
                  <p className="text-xs text-slate-400">Total Bids</p>
                </div>
              </div>

              {/* Leaderboard */}
              {leaderboard.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-slate-400 mb-2">Current Rankings</p>
                  <div className="space-y-2">
                    {leaderboard.slice(0, 3).map((bid, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center justify-between p-2 rounded-lg ${
                          idx === 0 ? 'bg-yellow-500/10 border border-yellow-500/30' :
                          idx === 1 ? 'bg-slate-500/10 border border-slate-500/30' :
                          'bg-amber-500/10 border border-amber-500/30'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                            idx === 0 ? 'bg-yellow-500 text-black' :
                            idx === 1 ? 'bg-slate-400 text-black' :
                            'bg-amber-600 text-white'
                          }`}>
                            {bid.rank}
                          </span>
                          <span className="text-white text-sm">{bid.bar_name}</span>
                        </div>
                        <span className="text-green-400 font-bold">${bid.bid_amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button 
                className="w-full bg-gradient-to-r from-pink-500 to-red-500"
                onClick={() => openBidDialog(currentWeek.week_date)}
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Place Your Bid
              </Button>
              {user && (
                <Button
                  data-testid="rsvp-btn-bidding"
                  className={`w-full mt-2 ${hasRsvp ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-700 hover:bg-slate-600'}`}
                  onClick={handleRsvp}
                  disabled={rsvpLoading}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  {hasRsvp ? `RSVP'd (${rsvpCount})` : `RSVP for Saturday (${rsvpCount} going)`}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* How It Works */}
      <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700 mb-4">
        <h3 className="font-semibold text-white mb-3">How Singles Night Works</h3>
        <div className="space-y-2 text-sm text-slate-400">
          <p>🏆 Bars bid to host the weekly Singles Night event</p>
          <p>💰 Highest bidder wins and gets featured to all NightBuZz users</p>
          <p>💕 Singles from across the city come to meet & mingle</p>
          <p>📈 Winning bars see massive foot traffic & revenue</p>
        </div>
      </div>

      {/* Bid Dialog */}
      <Dialog open={bidDialogOpen} onOpenChange={setBidDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-500" />
              Bid for Singles Night
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Bid Amount ($)</label>
              <Input
                type="number"
                min="50"
                step="25"
                value={bidForm.bid_amount}
                onChange={(e) => setBidForm({ ...bidForm, bid_amount: parseFloat(e.target.value) })}
                className="bg-slate-800 border-slate-700"
              />
              <p className="text-xs text-slate-500 mt-1">Minimum bid: $50</p>
            </div>
            
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Max Capacity</label>
              <Input
                type="number"
                min="20"
                value={bidForm.max_capacity}
                onChange={(e) => setBidForm({ ...bidForm, max_capacity: parseInt(e.target.value) })}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Drink Specials</label>
              <Input
                placeholder="e.g., $5 cocktails, 2-for-1 beers"
                value={bidForm.drink_specials}
                onChange={(e) => setBidForm({ ...bidForm, drink_specials: e.target.value })}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Event Description (optional)</label>
              <Input
                placeholder="What makes your bar perfect for singles?"
                value={bidForm.description}
                onChange={(e) => setBidForm({ ...bidForm, description: e.target.value })}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            <Button
              className="w-full bg-gradient-to-r from-pink-500 to-red-500"
              onClick={handlePlaceBid}
            >
              Place ${bidForm.bid_amount} Bid
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SinglesNight;
